import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Image,
  Link,
  List,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import datacenterImage from '~/assets/illustrations/datacenter.jpg'
import { cardHover, PageHeader, SectionHeading } from '~/components/shell'

export const Route = createFileRoute('/_app/datacenter/')({ component: DatacenterDemo })

function DatacenterDemo() {
  const navigate = useNavigate()

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader title="Data Center" subtitle="Infrastructure monitoring and operations" />

      <VStack align="stretch" gap={10}>
        {/* Customer story */}
        <Card.Root overflow="hidden" boxShadow="2xl">
          <Stack direction={{ base: 'column', md: 'row' }}>
            <Image
              objectFit="cover"
              maxW={{ base: '100%', md: '300px' }}
              src={datacenterImage}
              alt="Data Center"
            />
            <Stack flex={1} mb="6">
              <Card.Header>
                <Heading size="lg" color="gray.600">
                  Achieving Five-Nines Uptime with Real-Time Infrastructure Intelligence
                </Heading>
              </Card.Header>
              <Card.Body>
                <VStack align="flex-start" gap={4} mb="4">
                  <Text>
                    NovaTech Data Services operates colocation facilities serving enterprise
                    customers with strict SLA requirements. Their legacy monitoring systems created
                    fragmented visibility across cooling, power, and environmental systems - leading
                    to reactive incident response, energy waste, and customer SLA risks.
                  </Text>
                  <Text>
                    By deploying the{' '}
                    <Link
                      href="https://www.hivemq.com/products/hivemq-platform/"
                      target="_blank"
                      rel="noreferrer"
                      color="blue.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      HiveMQ Platform
                    </Link>
                    , NovaTech unified real-time telemetry from 50,000+ sensors across their
                    facilities into a single data backbone. HiveMQ Edge bridges BACnet and Modbus
                    systems from HVAC and power infrastructure, while the enterprise broker ensures
                    sub-second alerting and analytics across all sites.
                  </Text>
                  <Text>
                    <Link
                      href="https://www.hivemq.com/products/hivemq-edge/"
                      target="_blank"
                      rel="noreferrer"
                      color="blue.500"
                      _hover={{ textDecoration: 'underline' }}
                    >
                      HiveMQ Edge
                    </Link>{' '}
                    seamlessly bridges legacy BACnet and Modbus equipment to modern systems, while
                    the enterprise broker ensures reliable data flow across all facilities -
                    enabling instant visibility from server rack to operations center.
                  </Text>
                  <Text fontWeight="medium">Business Impact Delivered:</Text>
                  <List.Root pl={4}>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>30% reduction in cooling costs</b> through real-time PUE optimization
                          and dynamic cooling adjustments
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>99.999% uptime achieved</b> with predictive alerting that catches
                          issues before they impact customers
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>15-minute faster incident response</b> via unified dashboards
                          eliminating tool-switching
                        </Text>
                      </HStack>
                    </List.Item>
                    <List.Item>
                      <HStack gap={2}>
                        <Box p={0.5} bg="green.100" borderRadius="full">
                          <Check size={12} color="var(--chakra-colors-green-600)" />
                        </Box>
                        <Text fontSize="sm">
                          <b>Multi-site visibility</b> enabling centralized operations for
                          geographically distributed facilities
                        </Text>
                      </HStack>
                    </List.Item>
                  </List.Root>
                </VStack>
              </Card.Body>
            </Stack>
          </Stack>
        </Card.Root>

        {/* Platform journey */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="The Industrial Data Platform for Agentic AI" />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Connect
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  50,000+ sensors across cooling, power, and environmental systems bridged via MQTT
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Contextualize
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Environmental and power data unified into a governed namespace for all facilities
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Analyze
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Cooling efficiency and SLA compliance monitored in real time across all sites
                </Text>
              </Card.Body>
            </Card.Root>
            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="md" color="brand.solid">
                  Act
                </Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Predictive alerts trigger corrective actions before incidents impact customers
                </Text>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* Data flow, UNS and AI */}
        <VStack align="stretch" gap={4}>
          <SectionHeading title="Data Flow, UNS and AI" />
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} gap={6}>
            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/datacenter/flow' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Single Facility</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Data center monitoring architecture for a single site with cooling, power, and
                  environmental systems.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/datacenter/flow' })
                  }}
                >
                  View Flow
                </Button>
              </Card.Footer>
            </Card.Root>
            {/*<Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="lg" mb="2">UNS Explorer</Heading>
              </Card.Header>
              <Card.Body mb="2">
                <Text>Explore the Data Center Unified Namespace in real-time.</Text>
              </Card.Body>
              <Card.Footer>
                <Button onClick={() => navigate({ to: '/datacenter/explorer' })} w="100%">
                  Explore UNS
                </Button>
              </Card.Footer>
            </Card.Root>

            <Card.Root boxShadow="lg">
              <Card.Header>
                <Heading size="lg" mb="2">AI Assistant</Heading>
              </Card.Header>
              <Card.Body mb="2">
                <Text>Chat with AI about data center operations and metrics.</Text>
              </Card.Body>
              <Card.Footer>
                <Button onClick={() => navigate({ to: '/datacenter/bot' })} w="100%">
                  Chat with AI
                </Button>
              </Card.Footer>
            </Card.Root>
            */}
            <Card.Root
              overflow="hidden"
              transition="all 0.3s"
              _hover={cardHover}
              cursor="pointer"
              onClick={() => navigate({ to: '/datacenter/knowledge-graph' })}
              h="100%"
            >
              <Card.Header>
                <Heading size="md">Knowledge Graph</Heading>
              </Card.Header>
              <Card.Body>
                <Text color="content.secondary">
                  Visualize the data center UNS as a real-time network graph.
                </Text>
              </Card.Body>
              <Card.Footer>
                <Button
                  w="100%"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate({ to: '/datacenter/knowledge-graph' })
                  }}
                >
                  View Graph
                </Button>
              </Card.Footer>
            </Card.Root>
          </SimpleGrid>
        </VStack>
      </VStack>
    </Box>
  )
}
